import React from 'react';
import { NavBadge } from './NavBadge';
import { Link, type Location, type NavigateFunction } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SrIcon } from '@/components/ui/SrIcon';
import { cn } from '@/lib/utils';
import logoFull from '@/assets/logos/solvrev-logo-full.png';
import logoMark from '@/assets/logos/solvrev-logo-mark.png';
import { isNavChildActive, type NavItem } from './nav-items';
import { useNavGroupState } from './use-nav-group-state';
import type { SidebarShortcut } from '@/hooks/use-sidebar-shortcuts';

interface AppSidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  filteredNav: NavItem[];
  location: Location;
  navigate: NavigateFunction;
  innstillingerOpen: boolean;
  setInnstillingerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shortcuts: SidebarShortcut[];
  isShortcutActive: (shortcutPath: string) => boolean;
}

export default function AppSidebar({
  collapsed,
  setCollapsed,
  filteredNav,
  location,
  navigate,
  innstillingerOpen,
  setInnstillingerOpen,
  shortcuts,
  isShortcutActive,
}: AppSidebarProps) {
  const groups = useNavGroupState(location, innstillingerOpen, setInnstillingerOpen);
  return (
    <aside
      className={cn(
        'hidden md:flex flex-col shrink-0 transition-[width] duration-200',
        'bg-sidebar text-sidebar-foreground border-r-[1.5px] border-[var(--sr-revemoerk)]',
        collapsed ? 'w-[68px]' : 'w-[240px]',
      )}
    >
      {/* Brand block */}
      <div
        className={cn(
          'flex items-center gap-3 pb-5 border-b border-white/10',
          collapsed ? 'justify-center px-0 pt-4' : 'px-[22px] pt-[18px]',
        )}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hover:opacity-80 transition-opacity shrink-0 flex items-center"
          aria-label={collapsed ? 'Utvid sidebar' : 'Skjul sidebar'}
        >
          {collapsed ? (
            <img src={logoMark} alt="Sølvrev" className="h-8 w-auto" />
          ) : (
            <img src={logoFull} alt="Sølvrev" className="h-8 w-auto" />
          )}
        </button>
        {!collapsed && <div className="flex-1 min-w-0" />}
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="p-1 rounded text-white/40 hover:text-white hover:bg-white/[0.08] transition-colors"
            aria-label="Skjul sidebar"
          >
            <SrIcon name="chevron-left" size={14} />
          </button>
        )}
      </div>

      {/* Navngitt landmark: skjermleser kan hoppe hit, og tester kan skille
          menyen fra sidens eget innhold. */}
      <nav aria-label="Hovedmeny" className="flex-1 py-3 overflow-y-auto">
        {filteredNav.map((item) => {
          // Collapsible group (Innstillinger og Salgsmuligheter).
          if (item.children) {
            const groupActive = location.pathname.startsWith(item.path);

            // Collapsed: render nøyaktig som et vanlig nav-ikon (samme element,
            // markup og klasser som lenkene over) — ingen gruppe-spesial-
            // formattering. Klikk åpner gruppa i state og navigerer til default
            // sub-rute.
            if (collapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <Link
                      to={groups.collapsedTarget(item)}
                      onClick={() => groups.open(item)}
                      className={cn(
                        'group relative flex items-center gap-[14px] text-[14px] transition-colors',
                        'justify-center px-0 py-2.5 mx-2 rounded',
                        'border-l-2 border-transparent',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                        groupActive
                          ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                          : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
                      )}
                    >
                      <SrIcon name={item.icon} size={18} />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs font-medium">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            // Expanded: collapsible gruppe med chevron + children.
            const isOpen = groups.isOpen(item);
            return (
              <div key={item.path}>
                <button
                  type="button"
                  onClick={() => groups.toggle(item)}
                  aria-expanded={isOpen}
                  className={cn(
                    'group relative flex w-full items-center gap-[14px] py-[10px] px-[22px] text-left text-[14px] transition-colors',
                    'border-l-2 border-transparent',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                    groupActive
                      ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                      : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
                  )}
                >
                  <SrIcon name={item.icon} size={18} />
                  <span className="flex-1">{item.label}</span>
                  <SrIcon
                    name={isOpen ? 'chevron-down' : 'chevron-right'}
                    size={12}
                    className="opacity-60"
                  />
                </button>
                {isOpen && (
                  <div className="mt-1 mb-1 space-y-0.5">
                    {item.children.map((child) => {
                      const childActive = isNavChildActive(child, item.children!, location.pathname);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          aria-current={childActive ? 'page' : undefined}
                          className={cn(
                            'group relative flex items-center gap-[10px] text-[13px] transition-colors pl-[52px] pr-[22px] py-[7px]',
                            'border-l-2 border-transparent',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                            childActive
                              ? 'bg-[rgb(3,30,255,0.14)] !border-[var(--sr-reveblaa)] text-white font-medium'
                              : 'text-white/60 hover:text-white hover:bg-white/[0.03]',
                          )}
                        >
                          <SrIcon name={child.icon} size={14} />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const active =
            location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const link = (
            <Link
              key={item.path}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'group relative flex items-center gap-[14px] text-[14px] transition-colors',
                collapsed ? 'justify-center px-0 py-2.5 mx-2 rounded' : 'py-[10px] px-[22px]',
                'border-l-2 border-transparent',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                active
                  ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                  : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
              )}
            >
              <SrIcon name={item.icon} size={18} />
              {!collapsed && <span>{item.label}</span>}
              <NavBadge count={item.badge} collapsed={collapsed} />
            </Link>
          );
          if (collapsed) {
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right" className="text-xs font-medium">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          }
          return link;
        })}

        {shortcuts.length > 0 && (
          <>
            <div className={cn('my-3 border-t border-white/10', collapsed ? 'mx-3' : 'mx-[22px]')} />
            {!collapsed && (
              <div className="px-[22px] pb-1 text-[10.5px] font-medium uppercase tracking-[0.09em] text-white/40">
                Snarveier
              </div>
            )}
            {shortcuts.map((s) => {
              const active = isShortcutActive(s.path);
              const link = (
                <Link
                  key={s.key}
                  to={s.path}
                  className={cn(
                    'group relative flex items-center gap-[14px] text-[13.5px] transition-colors',
                    collapsed ? 'justify-center px-0 py-2 mx-2 rounded' : 'py-2 px-[22px]',
                    'border-l-2 border-transparent',
                    active
                      ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]',
                  )}
                >
                  <SrIcon name={s.icon} size={16} />
                  {!collapsed && <span>{s.label}</span>}
                </Link>
              );
              if (collapsed) {
                return (
                  <Tooltip key={s.key}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" className="text-xs font-medium">
                      {s.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            })}
          </>
        )}
      </nav>
    </aside>
  );
}
